import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link, NavLink, useLocation } from 'react-router-dom'
import constants from 'config/constants'
import { useNavbarMenuContext } from './context/index.js'
import * as R from 'ramda'
import useModal from 'hooks/useModal'
import { Sidebar, Popup as SemanticPopup, Dropdown } from 'semantic-ui-react'
import MobileMenu from './MobileMenu'
import { AllowPermission } from 'shared/components/AllowPermission'
import { InitialName } from 'components/InitialName'
import { Loader } from 'shared/components/Loader'
import useSubMenu from 'hooks/useSubMenu'
import CustomDashboardModal from 'pages/Dashboards/CustomDashboardModal/index.js'
import FeedbackSubMenu, { isFeedbackMenu } from './FeedbackSubMenu'
import ReportSubMenu from './ReportSubMenu'
import TeamMembersSubMenu, { isTeamMembersMenu } from './TeamMembersSubMenu'
import { hasSubMenuRouting } from 'shared/utils/hasSubMenuRouting'
import MainNavButton from './MainNavButton'
import { TooltipLabelWithoutIcon } from 'components/TooltipLabelWithoutIcon/index.js'
import useAxios from 'hooks/useAxios/index.js'
import { surveyDashboardContext } from 'pages/Dashboards/SurveyDashboard/context'
import { FavoriteDashboardSurveyList } from './FavoriteDashboardSurverList.js'
import { useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import usePermission from 'hooks/usePermission/index.js'
import { ArchivedCustomDashboardContainer } from 'components/ArchivedCustomDashboardModel/container.js'
import { CrownBadge } from 'components/ProFeatureLabel/index.js'
import useWorkspace from 'hooks/useWorkspace/index.js'
import store from 'store'
import { getAxios } from 'api'
import { toast } from 'libs/toast'
import auth from 'auth'
import generateViewFilterToken from 'libs/generateViewFilterToken/index.js'
import useFilterOptions from 'hooks/useFilterOptions/index.js'
import Popup from 'shared/components/Popup'
import { getWorkspaceWeekStartDay } from 'libs/date'

/**
 *
 * @param {object} props
 * @param {() => void} props.onClickLogout
 * @param {boolean} props.isNavBarActive
 * @param {() => void} props.toggleNavBar
 * @param {object=} props.user
 * @param {string} props.workspaceToken
 */
export const NavBar = ({ onClickLogout, isNavBarActive, toggleNavBar, user, workspaceToken }) => {
  const { isShowSubMenu, setIsShowSubMenu, setShouldShowToggleIcon } = useSubMenu()
  const isMobileMenu = useMediaQuery({ query: '(max-width: 767px)' })
  const homePageUrl =
    useSelector((state) => R.path(['pages', 'dashboard', 'homePageUrl'], state)) ||
    '/dashboards/home/'
  const location = useLocation()
  const { hasPermission } = usePermission()

  const userRole = R.prop('role', user)
  const userFullName = user && R.prop('first_name', user) + ' ' + R.prop('last_name', user)
  const homeRef = React.createRef()

  const openSubMenu = (e) => {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      return
    }
    const shouldShowSubMenu = hasSubMenuRouting(location.pathname)

    if (shouldShowSubMenu) {
      setIsShowSubMenu(true)
      setShouldShowToggleIcon(false)
    }
  }

  const handleMainMenuChange = (e) => {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      return
    }
    setIsShowSubMenu(false)
    setShouldShowToggleIcon(false)
  }

  const url = location.pathname

  useEffect(() => {
    if (location.pathname !== '/') {
      // should open submenu by default when user entering url
      const isShowSubMenu = hasSubMenuRouting(location.pathname)
      setIsShowSubMenu(isShowSubMenu)
    }
  }, [location.pathname, setIsShowSubMenu])

  if (isMobileMenu) {
    return (
      <MobileMenu
        toggleNavBar={toggleNavBar}
        isNavBarActive={isNavBarActive}
        homePageUrl={homePageUrl}
        onClickLogout={onClickLogout}
        user={user}
      />
    )
  }

  return (
    <React.Fragment>
      <SidebarContainer className="submenu-transition" submenu={isShowSubMenu}>
        <StyledSidebar width="very thin" className="flex flex-col bg-white" visible>
          <div className="mt-[21px] mb-[54px] flex justify-center">
            <Link to={homePageUrl}>
              <img
                src={constants.simplesatLogoGreenUrl}
                alt="simplesat-logo"
                className="w-6 cursor-pointer"
              />
            </Link>
          </div>

          <StyledMainMenuIconContainer>
            {hasPermission(constants.permissions.viewDashboards) && (
              <MainNavButton
                innerRef={homeRef}
                name="dashboards"
                customcolor="rgba(122, 217, 154, 1)"
                custombg="rgba(122, 217, 154, 0.1)"
                icon="far fa-grid-2"
                to={{
                  pathname: `/${workspaceToken}/dashboards/home`,
                }}
                popupText="Dashboards"
                onClick={openSubMenu}
                isActive={location.pathname.includes('/dashboards/')}
              />
            )}
            {hasPermission(constants.permissions.viewFeedback) && (
              <MainNavButton
                name="feedback"
                customcolor="rgba(102, 178, 255, 1)"
                custombg="rgba(102, 178, 255, 0.1)"
                icon="far fa-comment-dots"
                to={{
                  pathname: `/${workspaceToken}/feedback/`,
                }}
                popupText="Feedback"
                onClick={openSubMenu}
                isActive={isFeedbackMenu(location.pathname)}
              />
            )}

            {hasPermission(constants.permissions.viewCustomers) && (
              <MainNavButton
                name="/customers/"
                customcolor="rgba(94, 222, 237, 1)"
                custombg="rgba(94, 222, 237, 0.1)"
                icon="far fa-user-friends"
                to={`/${workspaceToken}/customers/`}
                popupText="Customers"
                onClick={handleMainMenuChange}
              />
            )}

            {hasPermission(constants.permissions.viewTeamMembers) && (
              <MainNavButton
                name="/team-members/"
                customcolor="#DC73FF"
                custombg="#F6E7FF"
                icon="far fa-users-class"
                to={`/${workspaceToken}/team-members/`}
                popupText="Team members"
                onClick={openSubMenu}
                isActive={isTeamMembersMenu(location.pathname)}
              />
            )}

            {hasPermission(constants.permissions.viewReports) && (
              <MainNavButton
                name="/reports/"
                customcolor="rgba(190, 154, 245, 1)"
                custombg="rgba(190, 154, 245, 0.1)"
                icon="fas fa-chart-column"
                to={`/${workspaceToken}/reports/`}
                popupText="Reports"
                onClick={openSubMenu}
                isActive={location.pathname.includes('/reports/')}
              />
            )}

            {hasPermission(constants.permissions.viewIntegrations) && (
              <MainNavButton
                name="/settings/integrations/"
                customcolor="rgba(255, 121, 137, 1)"
                custombg="rgba(255, 121, 137, 0.1)"
                icon="fas fa-exchange-alt"
                to={`/${workspaceToken}/settings/integrations/`}
                popupText="Integrations"
                onClick={handleMainMenuChange}
              />
            )}
          </StyledMainMenuIconContainer>

          <StyledMainMenuIconContainer className="mt-auto">
            {hasPermission(constants.permissions.viewSuccessDashboard) && (
              <MainNavButton
                name="/success-dashboard/"
                customcolor="#FD7F52"
                custombg="#FDEAD7"
                icon="far fa-bullseye-arrow"
                to={`/${workspaceToken}/success-dashboard/`}
                popupText="Success dashboard"
                onClick={handleMainMenuChange}
                isActive={location.pathname.includes('/success-dashboard/')}
              />
            )}
            {hasPermission(constants.permissions.viewSettings) && (
              <StyledPopupMenu
                position="left center"
                offset={[
                  hasPermission(constants.permissions.viewSuccessDashboard) ? -220 : -200,
                  0,
                ]}
                style={{ padding: '0px' }}
                hoverable
                trigger={
                  <StyledMainMenuIcon
                    name="settings"
                    className="icon-menu"
                    customcolor="rgba(255, 200, 109, 1)"
                    custombg="rgba(255, 200, 109, 0.1)"
                    onClick={openSubMenu}
                    to={`/${workspaceToken}/settings/`}
                    isActive={(match, location) =>
                      match && !location.pathname.includes('/settings/integrations/')
                    }
                  >
                    <i className="fas fa-cog text-2xl my-1.5" />
                  </StyledMainMenuIcon>
                }
                popperModifiers={[
                  { name: 'preventOverflow', options: { boundariesElement: 'window' } },
                ]}
              >
                <SemanticPopup.Content>
                  <AdminMenu workspaceToken={workspaceToken} />
                </SemanticPopup.Content>
              </StyledPopupMenu>
            )}

            <StyledPopupMenu
              position="right center"
              offset={[-180, 0]}
              style={{ padding: '0px' }}
              hoverable
              trigger={
                <StyledMainMenuIcon
                  name="profile"
                  className="icon-menu"
                  onClick={openSubMenu}
                  to={`/${workspaceToken}/profile/edit/`}
                >
                  <InitialName name={userFullName} fontSize={0.825} className="font-bold my-1.5" />
                </StyledMainMenuIcon>
              }
              popperModifiers={[
                { name: 'preventOverflow', options: { boundariesElement: 'window' } },
              ]}
            >
              <SemanticPopup.Content>
                <ProfileMenu
                  onClickLogout={onClickLogout}
                  workspaceToken={workspaceToken}
                  workspaceName={R.path(['selected_workspace', 'name'], user)}
                />
              </SemanticPopup.Content>
            </StyledPopupMenu>
          </StyledMainMenuIconContainer>
        </StyledSidebar>
        <StyledSidebarSubMenu
          className="sub-menu"
          animation="push"
          width="wide"
          visible={isShowSubMenu}
        >
          {userRole !== undefined && (
            <div className="ml-[64px] p-2 text-left overflow-x-hidden">
              {url.includes('/dashboards/') || url.includes('/success-dashboard/') ? (
                <DashBoardSubMenu user={user} />
              ) : null}
              {url.includes('/settings/') && !url.includes('/settings/integrations/') && (
                <AdminSubMenu workspaceToken={workspaceToken} />
              )}
              {url.includes('/profile') && <ProfileSubMenu workspaceToken={workspaceToken} />}
              {isFeedbackMenu(url) && <FeedbackSubMenu />}
              {isTeamMembersMenu(url) && <TeamMembersSubMenu />}
              {url.includes('/reports/') && <ReportSubMenu />}
            </div>
          )}
        </StyledSidebarSubMenu>
      </SidebarContainer>
    </React.Fragment>
  )
}

export const LinkButton = styled.a`
  user-select: none;
  font-size: 14px;
  cursor: pointer;
  color: #9b9b9b;
  border: none;
  text-align: left;
  margin-left: 10px;

  &:hover {
    color: var(--blue-dark);
  }
  &.disable {
    &:hover {
      color: #9b9b9b;
    }
  }
`

const DashBoardSubMenu = ({ user }) => {
  const { hasPermission } = usePermission()

  const hasViewCustomDashboardsPermisison = hasPermission(
    constants.permissions.viewCustomDashboards
  )
  const hasViewSurveyDashboardsPermission = hasPermission(
    constants.permissions.viewSurveyDashboards
  )

  const workspaceToken = useWorkspace()

  return (
    <div className="ui borderless vertical menu fluid secondary !border-0 !m-0 ">
      <HeaderWithSidebarIcon>Dashboards</HeaderWithSidebarIcon>
      <StyledSubMenuItem
        className="header-item item"
        isActive={(_, location) => location.pathname === `/${workspaceToken}/dashboards/home/`}
        to={{
          pathname: `/${workspaceToken}/dashboards/home`,
          state: { useStickyFilter: false, dashboardName: null },
        }}
      >
        <h5 className="flex items-center">Overview</h5>
      </StyledSubMenuItem>

      <FavoriteDashboardSurveyList
        workspaceToken={workspaceToken}
        hasViewCustomDashboardsPermisison={hasViewCustomDashboardsPermisison}
        hasViewSurveyDashboardsPermission={hasViewSurveyDashboardsPermission}
      />

      <DashboardSubMenuCustomDashboard user={user} workspaceToken={workspaceToken} />

      {hasViewSurveyDashboardsPermission && (
        <DashboardSubMenuSurveyDashboardView workspaceToken={workspaceToken} />
      )}
    </div>
  )
}

const DashboardSubMenuCustomDashboard = ({ user, workspaceToken }) => {
  const { Modal, open, isOpen, close } = useModal()
  const context = useNavbarMenuContext()
  const contextData = R.prop('contextData', context)
  const customDashboardsLimit = R.path(
    ['subscription', 'subscription_items_metadata', 'custom_dashboards'],
    user
  )
  const dashboardContext = useContext(surveyDashboardContext)
  const setContext = R.prop('setContext', dashboardContext)

  const {
    data: widgetSurveyList,
    loading: widgetSurveyLoading,
    error: widgetSurveyError,
  } = useAxios({
    url: 'dashboard/dashboard/widget/surveys/',
  })

  useEffect(() => {
    if (setContext) {
      if (widgetSurveyList) {
        setContext({
          widgetSurvey: widgetSurveyList,
          widgetSurveyLoading: false,
        })
      } else if (widgetSurveyError) {
        setContext({
          widgetSurvey: [],
          widgetSurveyLoading: false,
        })
      }
    }
  }, [widgetSurveyList, widgetSurveyLoading, setContext, widgetSurveyError])

  const { data: featureToggle } = useAxios({
    method: 'post',
    url: 'billing/plans/feature-toggle/',
    data: { feature: 'multiple_widget_datasets' },
  })

  useEffect(() => {
    if (featureToggle && setContext) {
      setContext({
        featureToggleState: { multipleWidgetDatasets: featureToggle.enabled },
      })
    }
  }, [featureToggle, setContext])

  if (!contextData || !user) {
    return null
  }

  const allowAddDashboard =
    contextData.customDashboards && !contextData.customDashboardsLoading
      ? !customDashboardsLimit ||
        parseInt(customDashboardsLimit) > contextData.customDashboards.results.length
      : false
  return (
    <React.Fragment>
      <CustomDashboardsHeader />
      {contextData.customDashboardsLoading && contextData.companyUsersLoading ? (
        <Loader className="ml-5 !z-[-1]" />
      ) : (
        <React.Fragment>
          {R.propOr([], 'results', contextData.customDashboards).map((dashboard) => {
            return (
              <Popup.Truncated key={dashboard.id} content={dashboard.name}>
                <StyledSubMenuItem
                  className="sub-menu-item item"
                  to={{
                    pathname: `/${workspaceToken}/dashboards/${dashboard.token}/`,
                    state: { useStickyFilter: false, dashboardName: dashboard.name },
                  }}
                >
                  {dashboard.name}
                </StyledSubMenuItem>
              </Popup.Truncated>
            )
          })}
          <AllowPermission permission={constants.permissions.manageCustomDashboards}>
            <NewDashboardButton
              enable={allowAddDashboard}
              open={open}
              customDashboardsLimit={customDashboardsLimit}
            />
          </AllowPermission>
          {isOpen && (
            <CustomDashboardModal modal={Modal} close={close} users={contextData.companyUsers} />
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

const NewDashboardButton = ({ enable, open, customDashboardsLimit }) => {
  const isDashboardLimitZero = parseInt(customDashboardsLimit) == 0

  if (enable) {
    return (
      <LinkButton
        onClick={(e) => {
          e.preventDefault()
          open()
        }}
      >
        <span>
          <i className="mx-1 far fa-plus" />
          New dashboard
        </span>
      </LinkButton>
    )
  } else {
    return (
      <TooltipLabelWithoutIcon
        className="tooltip"
        descriptionTitle={
          <span className="flex">
            {' '}
            {isDashboardLimitZero ? 'Custom dashboards' : 'Dashboard limit reached'} <CrownBadge />
          </span>
        }
        descriptionContent={
          isDashboardLimitZero
            ? 'Upgrade your subscription to unlock this feature.'
            : 'Upgrade your subscription to add more custom dashboards.'
        }
        showGotoBillingPage={true}
        trigger={
          <LinkButton className="disable">
            <span>
              <i className="mx-1 far fa-plus" />
              New dashboard
            </span>
          </LinkButton>
        }
        learnMoreLink="https://help.simplesat.io/en/articles/8856031-custom-dashboards"
      />
    )
  }
}
const DashboardSubMenuSurveyDashboardView = ({ workspaceToken }) => {
  const context = useNavbarMenuContext()
  const contextData = R.prop('contextData', context)
  const updateContextData = R.prop('updateContextData', context)
  const [loading, setLoading] = useState(false)
  const weekStartDay = getWorkspaceWeekStartDay()

  function loadMoreSurveyView() {
    setLoading(true)
    getAxios()
      .get('survey/views/?limit=1000')
      .then((res) => {
        const newSurveyView = R.path(['results'], res.data)
        updateContextData('survey', newSurveyView)
        setLoading(false)
      })
      .catch(() => {
        toast.error('Oops! Something went wrong. Please try again.')
        setLoading(false)
      })
  }

  const options = useFilterOptions('surveyDashboard')

  if (!contextData) {
    return null
  }
  const surveyView = contextData?.surveyDashboardViews || []

  return (
    <React.Fragment>
      <h5 className="sub-menu-title item">Surveys</h5>
      {!contextData.companyUsersLoading && contextData.surveyDashboardViewsLoading ? (
        <Loader className="!z-[-1]" withContainer />
      ) : (
        surveyView.map((mainSurvey) => {
          return (
            <React.Fragment key={mainSurvey.id}>
              <Popup.Truncated content={mainSurvey.survey.name}>
                <StyledSubMenuItem
                  className="sub-menu-item item"
                  isActive={(_, location) =>
                    location.pathname.includes(
                      `/${workspaceToken}/dashboards/views/${mainSurvey.token}`
                    )
                  }
                  to={{
                    pathname: `/${workspaceToken}/dashboards/views/${mainSurvey.token}/overview/`,
                    state: { useStickyFilter: false, surveyToken: mainSurvey.survey.token },
                    search: generateViewFilterToken(mainSurvey, options, weekStartDay),
                  }}
                >
                  {mainSurvey.survey.name}
                </StyledSubMenuItem>
              </Popup.Truncated>
              {mainSurvey.views.map((view) => {
                const filtertToken = generateViewFilterToken(view, options, weekStartDay)
                return (
                  <Popup.Truncated key={view.id} content={view.name}>
                    <StyledSubMenuSubItem
                      className="sub-menu-item item"
                      isActive={(_, location) =>
                        location.pathname.includes(`/dashboards/views/${view.token}`)
                      }
                      to={{
                        pathname: `/${workspaceToken}/dashboards/views/${view.token}/overview/`,
                        state: { useStickyFilter: false, surveyToken: mainSurvey.survey.token },
                        search: filtertToken,
                      }}
                    >
                      <i className="fas fa-circle text-grey" />
                      {view.name}
                    </StyledSubMenuSubItem>
                  </Popup.Truncated>
                )
              })}
            </React.Fragment>
          )
        })
      )}
      {contextData.surveyDashboardViewsReachLimit && !loading && (
        <LinkButton
          onClick={(e) => {
            e.preventDefault()
            loadMoreSurveyView()
          }}
        >
          <span className="ml-1">View all</span>
        </LinkButton>
      )}
      {loading && <Loader className="!z-[-1]" withContainer />}
    </React.Fragment>
  )
}

const CustomDashboardsHeader = () => {
  const { Modal, open, close } = useModal()
  const [isArchivedMenuOpen, setIsArchivedMenuOpen] = useState(false)
  const [isShowArchivedKebab, setIsShowArchivedKebab] = useState(false)
  const { hasPermission } = usePermission()

  return (
    <React.Fragment>
      <div
        className="sub-menu-title item"
        onMouseOver={() => setIsShowArchivedKebab(true)}
        onMouseLeave={() => setIsShowArchivedKebab(false)}
      >
        <h5 className="sub-menu-title-text">
          Custom dashboards
          {isShowArchivedKebab && hasPermission(constants.permissions.manageCustomDashboards) && (
            <SemanticPopup
              basic
              position="bottom left"
              open={isArchivedMenuOpen}
              on="click"
              onOpen={() => setIsArchivedMenuOpen(true)}
              onClose={() => setIsArchivedMenuOpen(false)}
              trigger={
                <Dropdown
                  icon="far fa-ellipsis-vertical"
                  floating
                  className="relative float-right widget-toolbar-dropdown top-1"
                />
              }
            >
              <div
                className="cursor-pointer"
                onClick={() => {
                  open()
                  setIsArchivedMenuOpen(false)
                }}
              >
                <span className="mr-2">
                  <i className="far fa-box-archive icon" />
                </span>
                View archived dashboards
              </div>
            </SemanticPopup>
          )}
        </h5>
      </div>
      <ArchivedCustomDashboardContainer Modal={Modal} onClose={close} />
    </React.Fragment>
  )
}

const AdminSubMenu = ({ workspaceToken }) => {
  const { hasPermission } = usePermission()
  const hasManageAuthenticationSecurity = hasPermission(
    constants.permissions.manageAuthenticationSecurity
  )
  const hasAccessKeyPermission = hasPermission(constants.permissions.accessApiKey)

  return (
    <div className="ui borderless vertical menu fluid secondary !border-0 !m-0">
      <HeaderWithSidebarIcon>Admin</HeaderWithSidebarIcon>
      <AllowPermission permission={constants.permissions.viewSettings}>
        <StyledSubMenuItem
          className="sub-menu-item item"
          to={`/${workspaceToken}/settings/workspace/`}
        >
          Workspace settings
        </StyledSubMenuItem>
        <StyledSubMenuItem
          className="sub-menu-item item"
          to={`/${workspaceToken}/settings/brands/`}
        >
          Brands
        </StyledSubMenuItem>
        <StyledSubMenuItem
          className="sub-menu-item item"
          to={`/${workspaceToken}/settings/surveys/`}
        >
          Surveys
        </StyledSubMenuItem>
        <StyledSubMenuItem
          className="sub-menu-item item"
          to={`/${workspaceToken}/settings/topics/`}
        >
          Topics
        </StyledSubMenuItem>

        <StyledSubMenuItem className="sub-menu-item item" to={`/${workspaceToken}/settings/roles/`}>
          Roles
        </StyledSubMenuItem>
        <StyledSubMenuItem className="sub-menu-item item" to={`/${workspaceToken}/settings/email/`}>
          Email authentication
        </StyledSubMenuItem>
        <StyledSubMenuItem
          className="sub-menu-item item"
          to={`/${workspaceToken}/settings/feedback-widgets/`}
        >
          Feedback widgets
        </StyledSubMenuItem>
        <StyledSubMenuItem className="sub-menu-item item" to={`/${workspaceToken}/settings/users/`}>
          Users
        </StyledSubMenuItem>

        {hasManageAuthenticationSecurity && (
          <StyledSubMenuItem
            className="sub-menu-item item"
            to={`/${workspaceToken}/settings/authentication/`}
          >
            Authentication security
          </StyledSubMenuItem>
        )}
        {hasAccessKeyPermission && (
          <StyledSubMenuItem
            className="sub-menu-item item"
            to={`/${workspaceToken}/settings/api-keys/`}
          >
            API keys
          </StyledSubMenuItem>
        )}

        <StyledSubMenuItem className="sub-menu-item item" to={`/${workspaceToken}/settings/usage/`}>
          Usage
        </StyledSubMenuItem>
      </AllowPermission>

      <AllowPermission permission={constants.permissions.viewBillingPage}>
        <StyledSubMenuItem
          className="sub-menu-item item"
          to={`/${workspaceToken}/settings/billing/`}
        >
          Billing
        </StyledSubMenuItem>
      </AllowPermission>
    </div>
  )
}

const ProfileMenu = ({ onClickLogout, workspaceToken, workspaceName }) => {
  return (
    <div className="ui borderless visible vertical menu compact !border-0">
      {!auth.isDemoUser() && (
        <React.Fragment>
          <div className="item !pb-0">
            <div className="header text-three-line-ellipsis">{workspaceName}</div>
          </div>
          <Link
            className="item"
            to="/workspaces/"
            onClick={() => {
              store.remove(constants.simplesatWorkspaceTokenName)
            }}
          >
            <i className="far fa-building icon grey !ml-0 !mr-0" />
            Change workspace
          </Link>
          <div className="ui divider fitted" />
        </React.Fragment>
      )}
      <Link className="item" to={`/${workspaceToken}/profile/edit/`}>
        Edit profile
      </Link>
      <Link className="item" to={`/${workspaceToken}/profile/security/`}>
        Security
      </Link>
      {!auth.isDemoUser() && (
        <>
          <AllowPermission permission={constants.permissions.viewProfileNotification}>
            <Link className="item" to={`/${workspaceToken}/profile/notifications/`}>
              Notifications
            </Link>
          </AllowPermission>
          <AllowPermission permission={constants.permissions.viewProfileSummary}>
            <Link className="item" to={`/${workspaceToken}/profile/summaries/`}>
              Email summaries
            </Link>
          </AllowPermission>
        </>
      )}

      <div className="ui divider fitted" />
      <a className="item" onClick={() => window.Intercom('show')}>
        <i className="far fa-comment-question icon !ml-0" />
        Get help
      </a>
      <a
        className="item"
        href="https://www.simplesat.io/release-notes"
        target="_blank"
        rel="noreferrer"
      >
        <i className="far fa-rocket icon !ml-0" />
        Release notes
      </a>
      <div className="ui divider fitted" />
      {!auth.isDemoUser() && (
        <a className="item" onClick={onClickLogout}>
          <i className="far fa-power-off icon !ml-0" />
          Log out
        </a>
      )}
    </div>
  )
}

const ProfileSubMenu = ({ workspaceToken }) => {
  return (
    <div className="ui borderless vertical menu fluid secondary !border-0 !m-0">
      <HeaderWithSidebarIcon>Profile</HeaderWithSidebarIcon>
      <StyledSubMenuItem className="sub-menu-item item" to={`/${workspaceToken}/profile/edit/`}>
        Edit profile
      </StyledSubMenuItem>
      <StyledSubMenuItem className="sub-menu-item item" to={`/${workspaceToken}/profile/security/`}>
        Security
      </StyledSubMenuItem>
      {!auth.isDemoUser() && (
        <>
          <AllowPermission permission={constants.permissions.viewProfileNotification}>
            <StyledSubMenuItem
              className="sub-menu-item item"
              to={`/${workspaceToken}/profile/notifications/`}
            >
              Notifications
            </StyledSubMenuItem>
          </AllowPermission>
          <AllowPermission permission={constants.permissions.viewProfileSummary}>
            <StyledSubMenuItem
              className="sub-menu-item item"
              to={`/${workspaceToken}/profile/summaries/`}
            >
              Email summaries
            </StyledSubMenuItem>
          </AllowPermission>
        </>
      )}
    </div>
  )
}

const AdminMenu = ({ workspaceToken }) => {
  const { hasPermission } = usePermission()
  const hasManageAuthenticationSecurity = hasPermission(
    constants.permissions.manageAuthenticationSecurity
  )
  const hasAccessKeyPermission = hasPermission(constants.permissions.accessApiKey)

  return (
    <div className="ui borderless vertical menu compact !border-0">
      <Link className="item" to={`/${workspaceToken}/settings/workspace/`}>
        Workspace settings
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/brands/`}>
        Brands
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/surveys/`}>
        Surveys
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/topics/`}>
        Topics
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/roles/`}>
        Roles
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/email/`}>
        Email authentication
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/feedback-widgets/`}>
        Feedback widgets
      </Link>
      <Link className="item" to={`/${workspaceToken}/settings/users/`}>
        Users
      </Link>

      {hasManageAuthenticationSecurity && (
        <Link className="item" to={`/${workspaceToken}/settings/authentication/`}>
          Authentication security
        </Link>
      )}
      {hasAccessKeyPermission && (
        <Link className="item" to={`/${workspaceToken}/settings/api-keys/`}>
          API keys
        </Link>
      )}

      <Link className="item" to={`/${workspaceToken}/settings/usage/`}>
        Usage
      </Link>

      <AllowPermission permission={constants.permissions.viewBillingPage}>
        <Link className="item" to={`/${workspaceToken}/settings/billing/`}>
          Billing
        </Link>
      </AllowPermission>
    </div>
  )
}

const SidebarContainer = styled.div`
  position: relative;
  flex: ${({ submenu }) => {
    return submenu === true ? '0 0 296px' : '0 0 64px'
  }};
  display: flex;

  @media (max-width: 767px) {
    display: none;
  }
`

const StyledSidebar = styled(Sidebar)`
  &&.ui.sidebar {
    overflow: visible !important;
    width: 64px !important;
  }

  &&.ui.left.visible.sidebar {
    -webkit-box-shadow: 1px 0px 0px #dee6f0;
    box-shadow: 1px 0px 0px #dee6f0;
  }
`

const StyledSidebarSubMenu = styled(Sidebar)`
  &&.sub-menu,
  &.ui.left.visible.sidebar {
    z-index: 3;
    display: flex;
    flex-direction: column;
    width: 354px;
    background: white;
    box-shadow: 4px 0px 12px rgba(88, 102, 126, 0.05);
    -webkit-transition: none !important;
    transition: none !important;
    overflow: hidden;
  }

  &&.ui.wide.left.sidebar {
    width: 296px;
    border-right: 1px solid rgba(88, 102, 126, 0.12);
  }
`

const StyledPopupMenu = styled(SemanticPopup)`
  &&.ui.right.center.popup:before {
    top: 93% !important;
  }

  &&.ui.popup:before {
    background: rgb(255, 255, 255);
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 0) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  .ui.menu .item {
    font-weight: 400 !important;
  }

  .ui.vertical.menu .item > i.icon {
    float: left !important;
    margin-right: 12px !important;
  }
`

const StyledMainMenuIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledMenuHeader = styled.h2`
  margin: 12px 0px 61px 15px !important;
  color: #172b4d !important;
`

export const StyledSubMenuItem = styled(NavLink)`
  &.header-item {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    padding-left: 13.125px !important;
    margin: 0px !important;
    color: #172b4d !important;
  }

  &.header-item > h5 {
    font-size: 16px !important;
  }

  &.sub-menu-item {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-weight: 400 !important;
    font-size: 14px;
    line-height: 1.25rem;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    margin: 0px !important;
    color: #172b4d !important;
  }

  &.active,
  &:hover {
    background: #f4f7f9 !important;
  }
`

const StyledSubMenuSubItem = styled(NavLink)`
  &.sub-menu-item {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-weight: 400 !important;
    font-size: 14px;
    line-height: 1.25rem;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    display: flex;
    align-items: center;
    margin: 0px !important;
    color: #172b4d !important;
  }

  &.active,
  &:hover {
    background: #f4f7f9 !important;
  }

  > i {
    font-size: 6px;
    margin-right: 10px;
  }
`

export const StyledSubMenuSectionHeader = styled.div`
  &.section-header {
    padding-top: 28px !important;
    padding-bottom: 8px !important;
    padding-left: 13.125px !important;
    margin: 0px !important;
    color: #172b4d !important;
  }
  &.section-header > h5 {
    font-size: 16px !important;
  }
`

export const StyledMainMenuIcon = styled(NavLink)`
  &.icon-menu {
    display: flex;
    width: 48px;
    height: 48px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: rgba(165, 173, 186, 1);
    border-radius: 4px;
    margin-bottom: 16px;
  }

  &.icon-menu:hover,
  &.icon-menu.active {
    background-color: ${({ custombg }) => {
      return custombg
    }};
    color: ${({ customcolor }) => {
      return customcolor
    }};
    transition: all 0.3s ease;
    -webkit-transition: all 0.3s ease;
  }
`

export const StyledLink = styled(Link)`
  display: inline-block;
  width: 50px;
  height: 50px;
  text-align: center;

  &:before {
    content: '';
    display: inline-block;
    height: 100%;
    vertical-align: middle;
  }

  > img {
    width: 24px;
    vertical-align: middle;
  }
`

export function HeaderWithSidebarIcon({ children }) {
  const { isShowSubMenu, isShowToggleIcon, setIsShowSubMenu, setShouldShowToggleIcon } =
    useSubMenu()

  function toggleSubMenu() {
    setIsShowSubMenu(!isShowSubMenu)
    setShouldShowToggleIcon(!isShowToggleIcon)
  }

  return (
    <StyledMenuHeader>
      <div className="flex justify-between">
        {children}
        <SemanticPopup
          basic
          size="mini"
          position="top center"
          style={{ padding: '8px', fontSize: '14px' }}
          inverted
          trigger={
            <button
              className="!mr-4 !rounded !px-2 !py-1  border-0 cursor-pointer bg-transparent hover:bg-[#F4F7F9] relative bottom-1"
              onClick={toggleSubMenu}
            >
              <img
                src={constants.simplesatOpenedSidebarUrl}
                alt="Side-bar-icon"
                style={{ width: '20px', marginTop: '2px' }}
              />
            </button>
          }
        >
          Close sidebar
        </SemanticPopup>
      </div>
    </StyledMenuHeader>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/NavBar/index.js